import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const emptyForm = { name: '', email: '', password: '', phone: '', role: 'guard' };

const roleColor = {
  admin:      { bg: '#ede9fe', color: '#5b21b6' },
  supervisor: { bg: '#dbeafe', color: '#1e40af' },
  guard:      { bg: '#dcfce7', color: '#166534' },
};

export default function Guards() {
  const { user: authUser } = useAuth();
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);   // null = adding, object = editing
  const [form, setForm]         = useState(emptyForm);
  const [saving, setSaving]     = useState(false);
  const [search, setSearch]     = useState('');

  const isAdmin = authUser?.role === 'admin';

  const load = () => {
    setLoading(true);
    axios.get('/api/users')
      .then(r => { setUsers(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // ── Open add form ──────────────────────────────────────────
  const openAdd = () => {
    setEditUser(null);
    setForm(emptyForm);
    setShowForm(true);
    setTimeout(() => document.getElementById('guard-form-name')?.focus(), 50);
  };

  // ── Open edit form ─────────────────────────────────────────
  const openEdit = (u) => {
    setEditUser(u);
    setForm({ name: u.name, email: u.email, password: '', phone: u.phone || '', role: u.role });
    setShowForm(true);
    setTimeout(() => document.getElementById('guard-form-name')?.focus(), 50);
  };

  const closeForm = () => { setShowForm(false); setEditUser(null); setForm(emptyForm); };

  // ── Submit: create or update ───────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editUser) {
        await axios.put(`/api/users/${editUser.id}`, form);
        toast.success('User updated successfully');
      } else {
        await axios.post('/api/users', form);
        toast.success('User created successfully');
      }
      closeForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle active/inactive ─────────────────────────────────
  const toggleUser = async (id) => {
    try {
      await axios.patch(`/api/users/${id}/toggle`);
      load();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const filtered = users.filter(u =>
    !search ||
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.role?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {/* ── HEADER ─────────────────────────────────────────── */}
      <div className="page-header">
        <div>
          <div className="page-title">👮 Guards & Staff</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 3 }}>
            {users.length} users · {users.filter(u => u.is_active).length} active
          </div>
        </div>
        {isAdmin && (
          <button className="btn btn-primary" onClick={openAdd}>+ Add User</button>
        )}
      </div>

      {/* ── ADD / EDIT MODAL ──────────────────────────────── */}
      {showForm && (
        <>
          <div onClick={closeForm} style={{
            position: 'fixed', inset: 0,
            background: 'rgba(15,20,50,0.45)', backdropFilter: 'blur(4px)', zIndex: 999,
          }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%',
            transform: 'translate(-50%,-50%)',
            width: 'min(92vw, 520px)',
            background: 'white', borderRadius: 18,
            boxShadow: '0 24px 80px rgba(0,0,0,0.2)',
            zIndex: 1000, overflow: 'hidden',
          }}>
            {/* Modal header */}
            <div style={{
              background: 'linear-gradient(135deg,#1a1a2e,#16213e)',
              padding: '18px 22px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: 'white' }}>
                  {editUser ? '✏️ Edit User' : '➕ Add New User'}
                </div>
                {editUser && (
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3 }}>
                    Editing: {editUser.name}
                  </div>
                )}
              </div>
              <button onClick={closeForm} style={{
                background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)',
                color: 'white', fontSize: 16, cursor: 'pointer',
                width: 32, height: 32, borderRadius: 8, lineHeight: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>✕</button>
            </div>

            {/* Form body */}
            <form onSubmit={handleSubmit} style={{ padding: '22px 22px 18px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Full Name *</label>
                  <input id="guard-form-name" className="form-input" value={form.name} required
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="John Smith" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Email *</label>
                  <input type="email" className="form-input" value={form.email} required
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    placeholder="john@company.com" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">
                    {editUser ? 'New Password' : 'Password *'}
                    {editUser && <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 6 }}>(leave blank to keep current)</span>}
                  </label>
                  <input type="password" className="form-input" value={form.password}
                    required={!editUser}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    placeholder={editUser ? '(unchanged)' : 'Min 6 characters'} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Phone</label>
                  <input className="form-input" value={form.phone}
                    onChange={e => setForm({ ...form, phone: e.target.value })}
                    placeholder="+91 98765 43210" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Role *</label>
                  <select className="form-input" value={form.role}
                    onChange={e => setForm({ ...form, role: e.target.value })}>
                    <option value="guard">Guard</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 6, borderTop: '1px solid #f0f2f5' }}>
                <button type="button" className="btn btn-secondary" onClick={closeForm}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : editUser ? '💾 Save Changes' : '➕ Create User'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* ── SEARCH + TABLE ──────────────────────────────────── */}
      <div className="card">
        {/* Search bar */}
        <div style={{ marginBottom: 16 }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="🔍  Search by name, email or role…"
            style={{
              padding: '9px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0',
              fontSize: 13, outline: 'none', width: '100%', maxWidth: 340,
              background: '#f8faff',
            }}
          />
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading…</div>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8', padding: 32 }}>
                      {search ? 'No users match your search' : 'No users yet'}
                    </td>
                  </tr>
                ) : filtered.map(u => {
                  const rc = roleColor[u.role] || { bg: '#f3f4f6', color: '#374151' };
                  return (
                    <tr key={u.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                          <div style={{
                            width: 32, height: 32, borderRadius: 10, flexShrink: 0,
                            background: rc.bg, color: rc.color,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 14, fontWeight: 700,
                          }}>
                            {u.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <span style={{ fontWeight: 600, color: '#1e293b' }}>{u.name}</span>
                        </div>
                      </td>
                      <td style={{ color: '#475569' }}>{u.email}</td>
                      <td style={{ color: '#64748b' }}>{u.phone || '—'}</td>
                      <td>
                        <span style={{
                          fontSize: 11, padding: '3px 10px', borderRadius: 20,
                          fontWeight: 700, background: rc.bg, color: rc.color,
                          textTransform: 'capitalize',
                        }}>{u.role}</span>
                      </td>
                      <td>
                        <span style={{
                          fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 700,
                          background: u.is_active ? '#dcfce7' : '#fee2e2',
                          color: u.is_active ? '#166534' : '#991b1b',
                        }}>
                          {u.is_active ? '✅ Active' : '⛔ Inactive'}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: '#94a3b8' }}>
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {isAdmin && (
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => openEdit(u)}
                              title="Edit user"
                            >
                              ✏️ Edit
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              className={`btn btn-sm ${u.is_active ? 'btn-danger' : 'btn-success'}`}
                              onClick={() => toggleUser(u.id)}
                              title={u.is_active ? 'Deactivate' : 'Activate'}
                            >
                              {u.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
